import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { API_ENDPOINTS } from '../config/api';
import './ViewJobs.css';

const EMPTY_FILTERS = { keyword: '', location: '', technology: '', experience: '', jobType: '', positionId: '', company: '' };

// Helper: detect if applyLink is an email address
const isEmail = (link) => {
  if (!link) return false;
  const trimmed = link.trim();
  // Check for email pattern (no http/https, contains @)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) || trimmed.startsWith('mailto:');
};

const getApplyHref = (link) => {
  if (!link) return '#';
  const trimmed = link.trim();
  if (trimmed.startsWith('mailto:')) return trimmed;
  if (isEmail(trimmed)) return `mailto:${trimmed}`;
  return trimmed;
};

export default function ViewJobs() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [expandedJob, setExpandedJob] = useState(null);

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [filterOptions, setFilterOptions] = useState({ locations: [], technologies: [], experienceLevels: [], jobTypes: [], companies: [] });
  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      const tm = setTimeout(() => {
        if (!isAuthenticated()) navigate('/signup');
        else setAuthChecked(true);
      }, 100);
      return () => clearTimeout(tm);
    }
    setAuthChecked(true);
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    (async () => {
      try {
        const res = await fetch(API_ENDPOINTS.JOBS.FILTERS, { credentials: 'include', headers: getAuthHeaders() });
        if (res.ok) setFilterOptions(await res.json());
      } catch (_) {}
    })();
  }, [authChecked]);

  const fetchJobs = useCallback(async (p = 0, f = appliedFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: p, size: 20 });
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${API_ENDPOINTS.JOBS.LIST}?${params}`, { credentials: 'include', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load jobs');
      const data = await res.json();
      setJobs(data.jobs || []);
      setPage(data.currentPage || 0);
      setTotalPages(data.totalPages || 1);
      setTotalElements(data.totalElements || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [appliedFilters]);

  useEffect(() => { if (authChecked) fetchJobs(0, EMPTY_FILTERS); }, [authChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    if (key === 'keyword' || key === 'positionId') {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => { setAppliedFilters(next); setPage(0); fetchJobs(0, next); }, 500);
    }
  };

  const applyFilters = () => { clearTimeout(debounceRef.current); setAppliedFilters({ ...filters }); setPage(0); fetchJobs(0, filters); };
  const clearFilters = () => { clearTimeout(debounceRef.current); setFilters({ ...EMPTY_FILTERS }); setAppliedFilters({ ...EMPTY_FILTERS }); setPage(0); fetchJobs(0, EMPTY_FILTERS); };
  const removeFilter = (key) => { const n = { ...filters, [key]: '' }; setFilters(n); setAppliedFilters(n); fetchJobs(0, n); };
  const activeFilterCount = Object.values(appliedFilters).filter(v => v).length;

  if (!authChecked) return null;

  return (
    <div className="view-jobs-page">
      <div className="view-jobs-container">

        {/* ─── Compact Header ─────────────────────────────────── */}
        <div className="vj-header">
          <div className="vj-header-left">
            <h1>💼 Careers</h1>
            <span className="vj-count-pill">{totalElements} open</span>
          </div>
          <p className="vj-header-sub">Explore opportunities &amp; build the future of AI</p>
        </div>

        {/* ─── Search + Filter Bar ────────────────────────────── */}
        <div className="vj-toolbar">
          <div className="vj-search">
            <svg className="vj-search-svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/></svg>
            <input
              type="text"
              placeholder="Search jobs..."
              value={filters.keyword}
              onChange={e => handleFilterChange('keyword', e.target.value)}
            />
            {filters.keyword && <button className="vj-search-clear" onClick={() => handleFilterChange('keyword', '')}>✕</button>}
          </div>
          <button className={`vj-filter-btn ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(v => !v)}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd"/></svg>
            Filters{activeFilterCount > 0 && <span className="vj-badge">{activeFilterCount}</span>}
          </button>
        </div>

        {/* ─── Filter Panel ───────────────────────────────────── */}
        {showFilters && (
          <div className="vj-filter-panel">
            <div className="vj-filter-grid">
              <div className="vj-fg">
                <label>Company</label>
                <select value={filters.company} onChange={e => handleFilterChange('company', e.target.value)}>
                  <option value="">All</option>
                  {filterOptions.companies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="vj-fg">
                <label>Location</label>
                <select value={filters.location} onChange={e => handleFilterChange('location', e.target.value)}>
                  <option value="">All</option>
                  {filterOptions.locations.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="vj-fg">
                <label>Technology</label>
                <select value={filters.technology} onChange={e => handleFilterChange('technology', e.target.value)}>
                  <option value="">All</option>
                  {filterOptions.technologies.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="vj-fg">
                <label>Experience</label>
                <select value={filters.experience} onChange={e => handleFilterChange('experience', e.target.value)}>
                  <option value="">All</option>
                  {filterOptions.experienceLevels.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="vj-fg">
                <label>Job Type</label>
                <select value={filters.jobType} onChange={e => handleFilterChange('jobType', e.target.value)}>
                  <option value="">All</option>
                  {filterOptions.jobTypes.map(jt => <option key={jt} value={jt}>{jt}</option>)}
                </select>
              </div>
              <div className="vj-fg">
                <label>Position ID</label>
                <input type="text" placeholder="BK-001" value={filters.positionId} onChange={e => handleFilterChange('positionId', e.target.value)} />
              </div>
              <div className="vj-fg vj-fg-actions">
                <button className="vj-btn-go" onClick={applyFilters}>Search</button>
                {activeFilterCount > 0 && <button className="vj-btn-reset" onClick={clearFilters}>Reset</button>}
              </div>
            </div>
          </div>
        )}

        {/* ─── Active Chips ───────────────────────────────────── */}
        {activeFilterCount > 0 && (
          <div className="vj-chips">
            {appliedFilters.company && <span className="vj-chip">{appliedFilters.company}<button onClick={() => removeFilter('company')}>✕</button></span>}
            {appliedFilters.location && <span className="vj-chip">{appliedFilters.location}<button onClick={() => removeFilter('location')}>✕</button></span>}
            {appliedFilters.technology && <span className="vj-chip">{appliedFilters.technology}<button onClick={() => removeFilter('technology')}>✕</button></span>}
            {appliedFilters.experience && <span className="vj-chip">{appliedFilters.experience}<button onClick={() => removeFilter('experience')}>✕</button></span>}
            {appliedFilters.jobType && <span className="vj-chip">{appliedFilters.jobType}<button onClick={() => removeFilter('jobType')}>✕</button></span>}
            {appliedFilters.positionId && <span className="vj-chip">ID: {appliedFilters.positionId}<button onClick={() => removeFilter('positionId')}>✕</button></span>}
            {appliedFilters.keyword && <span className="vj-chip">"{appliedFilters.keyword}"<button onClick={() => removeFilter('keyword')}>✕</button></span>}
            <button className="vj-clear-all" onClick={clearFilters}>Clear all</button>
          </div>
        )}

        {/* ─── Content ────────────────────────────────────────── */}
        {loading ? (
          <div className="vj-loading"><div className="vj-spinner" /><p>Loading positions...</p></div>
        ) : error ? (
          <div className="vj-status-msg error">
            <p>⚠️ {error}</p>
            <button onClick={() => fetchJobs(0, appliedFilters)} className="vj-btn-go">Retry</button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="vj-status-msg">
            <div className="vj-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </div>
            <h3>{activeFilterCount > 0 ? 'No matching positions' : 'No open positions right now'}</h3>
            <p>{activeFilterCount > 0 ? 'Try adjusting your filters' : 'Check back soon for new opportunities!'}</p>
            {activeFilterCount > 0 && <button className="vj-btn-go" onClick={clearFilters} style={{ marginTop: 8 }}>Clear Filters</button>}
          </div>
        ) : (
          <div className="vj-list">
            {jobs.map(job => (
              <div key={job.id} className={`vj-job ${expandedJob === job.id ? 'open' : ''}`}>
                {/* Top row */}
                <div className="vj-job-top" onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                  <div className="vj-job-main">
                    <div className="vj-job-title-row">
                      <h3>{job.title}</h3>
                      {job.company && <span className="vj-company">{job.company}</span>}
                      {job.positionId && <span className="vj-pid">{job.positionId}</span>}
                    </div>
                    <div className="vj-job-tags">
                      {job.location && <span className="vj-tag loc">📍 {job.location}</span>}
                      {job.experienceRequired && <span className="vj-tag exp">🎯 {job.experienceRequired}</span>}
                      {job.jobType && <span className="vj-tag type">{job.jobType}</span>}
                      {job.qualification && <span className="vj-tag qual">🎓 {job.qualification}</span>}
                      <span className="vj-tag date">
                        {job.createdAt ? new Date(job.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                      </span>
                    </div>
                    {job.technology && (
                      <div className="vj-techs">
                        {job.technology.split(',').slice(0, 5).map((t, i) => <span key={i} className="vj-tech">{t.trim()}</span>)}
                        {job.technology.split(',').length > 5 && <span className="vj-tech more">+{job.technology.split(',').length - 5}</span>}
                      </div>
                    )}
                  </div>
                  <div className="vj-job-actions">
                    {job.applyLink ? (
                      isEmail(job.applyLink) ? (
                        <a href={getApplyHref(job.applyLink)} className="vj-apply email" onClick={e => e.stopPropagation()} title="Send email">
                          📧 {job.applyLink.replace('mailto:', '')}
                        </a>
                      ) : (
                        <a href={getApplyHref(job.applyLink)} target="_blank" rel="noopener noreferrer" className="vj-apply" onClick={e => e.stopPropagation()}>Send CV / Apply</a>
                      )
                    ) : null}
                    <span className="vj-chevron">{expandedJob === job.id ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded detail panel */}
                {expandedJob === job.id && (
                  <div className="vj-job-detail">
                    <div className="vj-detail-divider" />

                    {/* Meta info grid */}
                    {(job.company || job.qualification || job.location || job.experienceRequired) && (
                      <div className="vj-detail-meta">
                        {job.company && (
                          <div className="vj-detail-meta-item">
                            <span className="vj-meta-icon">🏢</span>
                            <span><strong>Company:</strong> {job.company}</span>
                          </div>
                        )}
                        {job.location && (
                          <div className="vj-detail-meta-item">
                            <span className="vj-meta-icon">📍</span>
                            <span><strong>Location:</strong> {job.location}</span>
                          </div>
                        )}
                        {job.qualification && (
                          <div className="vj-detail-meta-item">
                            <span className="vj-meta-icon">🎓</span>
                            <span><strong>Qualification:</strong> {job.qualification}</span>
                          </div>
                        )}
                        {job.experienceRequired && (
                          <div className="vj-detail-meta-item">
                            <span className="vj-meta-icon">🎯</span>
                            <span><strong>Experience:</strong> {job.experienceRequired}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description section */}
                    <div className="vj-detail-section-label">Job Description / Skills</div>
                    <div className="vj-detail-text">
                      {job.description ? job.description : (
                        job.applyLink ? (
                          <span style={{ color: '#7a8ea0', fontStyle: 'italic' }}>
                            Click on the link below to get more details about this position.
                          </span>
                        ) : (
                          <span style={{ color: '#7a8ea0', fontStyle: 'italic' }}>
                            No description available.
                          </span>
                        )
                      )}
                    </div>

                    {/* Apply CTA */}
                    {job.applyLink && (
                      <div className="vj-detail-cta">
                        {isEmail(job.applyLink) ? (
                          <a href={getApplyHref(job.applyLink)} className="vj-apply full email">
                            📧 Send CV to {job.applyLink.replace('mailto:', '')} →
                          </a>
                        ) : (
                          <a href={getApplyHref(job.applyLink)} target="_blank" rel="noopener noreferrer" className="vj-apply full">
                            Send CV / Apply for this position →
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="vj-pager">
            <button disabled={page === 0} onClick={() => fetchJobs(page - 1, appliedFilters)}>← Prev</button>
            <span>{page + 1} / {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => fetchJobs(page + 1, appliedFilters)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
